import * as React from 'react'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { buttonVariants } from '@/components/ui/button'

interface ConfirmOptions {
  title: string
  description?: string
  /** The confirming button's label, e.g. "Delete container". */
  action: string
  destructive?: boolean
}

/**
 * `window.confirm`, drawn as a Helm dialog.
 *
 * Returns a `confirm` that resolves true or false the way the browser's does,
 * so a call site stays `if (!(await confirm({...}))) return`. Render `dialog`
 * once anywhere in the component.
 */
export function useConfirm() {
  const [options, setOptions] = React.useState<ConfirmOptions | null>(null)
  const resolver = React.useRef<((confirmed: boolean) => void) | null>(null)

  const confirm = React.useCallback((next: ConfirmOptions) => {
    // A second request answers the first with "no" rather than leaving it hanging.
    resolver.current?.(false)
    setOptions(next)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const settle = React.useCallback((confirmed: boolean) => {
    resolver.current?.(confirmed)
    resolver.current = null
    setOptions(null)
  }, [])

  const dialog = (
    <AlertDialog open={options !== null} onOpenChange={(open) => !open && settle(false)}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{options?.title}</AlertDialogTitle>
          {options?.description && (
            <AlertDialogDescription>{options.description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({
              variant: options?.destructive ? 'destructive' : 'default',
            })}
            onClick={() => settle(true)}
          >
            {options?.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )

  return { confirm, dialog }
}
